import { useEffect, useState } from 'react'

import { UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  ColorScheme,
  FacilityIcon,
  Flex,
  Text,
  errorColorFor,
} from '@hivewatch/dark'

import {
  ControlledSelect,
  ControlledTextInput,
  DarkAddressSearch,
  PinMarker,
  StaticMapBox,
} from '@/components/ui'
import { FacilityType } from '@/graphql/generated/schemas'

import {
  FormInputsIProps,
  LabelValueIProps,
  LatLngStateIProps,
} from '../types/types'
import { latRegex, lonRegex } from '../utils/utils'

interface DarkCreateFacilityFormIProps {
  form: UseFormMethods<FormInputsIProps>
  facilityTypes: LabelValueIProps[]
  isOpen: boolean
  colorScheme: ColorScheme
}

/**
 * Rebrand variant of {@link CreateFacilityForm}. The Market-type branch, the
 * coordinate validators and the map reveal are unchanged; the fields move from
 * uncontrolled `register` inputs to the controlled dark TextInput/Select, which
 * is the only way to bind them.
 */
export const DarkCreateFacilityForm = ({
  form,
  facilityTypes,
  isOpen,
  colorScheme,
}: DarkCreateFacilityFormIProps) => {
  const { t } = useTranslation()
  const {
    errors,
    control,
    trigger,
    getValues,
    setValue,
    register,
    unregister,
    reset,
    watch,
  } = form
  const type = watch('type')?.value
  const { latitude, longitude } = watch(['latitude', 'longitude', 'address'])

  const [latLng, setLatLng] = useState<LatLngStateIProps | null>(null)
  const [shouldShowMap, setShouldShowMap] = useState(false)

  const isMarket = type === FacilityType.Market

  // `address` is written with setValue rather than rendered through a
  // Controller, so it needs registering by hand — without it a Normal facility
  // with valid coordinates but no address passes `isValid` and submits
  // `address: null`. Latitude and longitude are Controllers whose validators
  // already short-circuit for Market.
  useEffect(() => {
    if (isMarket) {
      reset({ ...getValues(), latitude: null, longitude: null, address: null })
      setShouldShowMap(false)
      unregister('address')
    } else {
      register('address', {
        required: t('facilities:crud_address_required'),
      })
    }
    trigger(['address'])
  }, [type])

  useEffect(() => {
    if (latRegex.test(latitude) && lonRegex.test(longitude)) {
      setLatLng({
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      })
      setShouldShowMap(true)
    }
  }, [latitude, longitude, type])

  useEffect(() => {
    if (!isOpen) {
      reset()
      setShouldShowMap(false)
      setLatLng(null)
    }
  }, [isOpen])

  const labelColor =
    colorScheme === 'light' ? 'light.text.primary' : 'text.primary'
  const errorColor = errorColorFor(colorScheme)

  return (
    <Flex direction='column' gap='16px'>
      <ControlledTextInput
        colorScheme={colorScheme}
        control={control}
        data-testid='CreateFacilityModal:name'
        errors={errors}
        label={t('common:facility_name')}
        name='name'
        placeholder={t('facilities:crud_facility_name_placeholder')}
        rules={{ required: t('facilities:crud_facility_name_required') }}
        width='100%'
      />

      <ControlledTextInput
        colorScheme={colorScheme}
        control={control}
        data-testid='CreateFacilityModal:shortName'
        errors={errors}
        label={t('facilities:crud_short_name_label')}
        name='shortName'
        placeholder={t('facilities:crud_short_name_placeholder')}
        rules={{
          required: t('facilities:crud_short_name_required'),
          maxLength: {
            value: 8,
            message: t('facilities:crud_short_name_max_length'),
          },
        }}
        width='100%'
      />

      <ControlledSelect
        colorScheme={colorScheme}
        control={control}
        data-testid='createFacility_facilityName'
        label={t('facilities:crud_facility_type_label')}
        name='type'
        options={facilityTypes}
        placeholder={t('facilities:crud_facility_type_placeholder')}
        rules={{ required: true }}
      />

      {/* AddressSearch has no dark label prop, so render one to match. */}
      <Box>
        <Text color={labelColor} mb='4px' textStyle='label'>
          {t('common:address')}
        </Text>
        <DarkAddressSearch
          colorScheme={colorScheme}
          disabled={isMarket}
          onSelectAddress={(address) => {
            // DarkAddressSearch clears the selection on every keystroke, so the
            // write has to revalidate — otherwise `isValid` stays true after
            // the required address is cleared and Save remains enabled.
            setValue('address', address?.value, { shouldValidate: true })
            if (address) {
              setValue('latitude', String(address.latitude))
              setValue('longitude', String(address.longitude))
              setLatLng({
                lat: address.latitude,
                lng: address.longitude,
              })
              setShouldShowMap(true)
              trigger(['latitude', 'longitude'])
            }
          }}
          showTooltip={false}
        />
        {errors.address?.message && (
          <Text color={errorColor} mt='4px' textStyle='label'>
            {errors.address.message}
          </Text>
        )}
      </Box>

      <Flex gap='12px'>
        <Box flex='1'>
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            disabled={isMarket}
            errors={errors}
            label={t('facilities:crud_latitude_label')}
            name='latitude'
            placeholder={t('facilities:crud_latitude_placeholder')}
            rules={{
              validate: (v) => {
                if (isMarket) return true
                if (!v) return t('facilities:crud_latitude_required')
                if (!latRegex.test(v))
                  return t('facilities:crud_latitude_invalid')
                return true
              },
            }}
            width='100%'
          />
        </Box>
        <Box flex='1'>
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            disabled={isMarket}
            errors={errors}
            label={t('facilities:crud_longitude_label')}
            name='longitude'
            placeholder={t('facilities:crud_longitude_placeholder')}
            rules={{
              validate: (v) => {
                if (isMarket) return true
                if (!v) return t('facilities:crud_longitude_required')
                if (!lonRegex.test(v))
                  return t('facilities:crud_longitude_invalid')
                return true
              },
            }}
            width='100%'
          />
        </Box>
      </Flex>

      {shouldShowMap && (
        // Mapbox GL mis-positions its canvas inside the dark Modal's
        // `filter: drop-shadow` wrapper and overlays the footer, swallowing
        // button clicks. `pointerEvents: none` lets those clicks through.
        <Box pointerEvents='none'>
          <StaticMapBox
            height='150px'
            latitude={latLng?.lat}
            longitude={latLng?.lng}
          >
            <PinMarker
              Icon={FacilityIcon}
              bgGradient='linear(to-r, #1CFDE5, #07DB94)'
              borderColor='#42CD70'
              fadedPulseBoxShadow='rgba(43, 210, 180, 0)'
              pulseBoxShadow='rgba(43, 210, 180, 0.4)'
            />
          </StaticMapBox>
        </Box>
      )}
    </Flex>
  )
}
